import { FC } from 'react';
import { Button, InlineLoading, InlineNotification } from '@carbon/react';
import { Renew } from '@carbon/react/icons';
import { useAtom, useAtomValue } from 'jotai';
import { parseUTCdateTime } from 'utils/dates';
import { refreshOutcomeAtom, requestRefreshLoadableAtom } from './atoms';
import type { RefreshState } from './hooks';
import styles from './page.module.scss';

interface RefreshBarProps {
  // Null before the first refresh; undefined until the first response lands.
  lastRefreshedAt: string | null | undefined;
  state: RefreshState;
}

const STATUS: Record<Exclude<RefreshState, 'idle'>, string> = {
  queued: 'Refresh queued',
  refreshing: 'Refreshing all assignments…',
};

// When the stored measurements were taken, and "Refresh now", which re-measures
// every assignment in the background. A refresh takes about a minute; the page
// waits for it to land (useRefreshState).
// Nothing until the first response; "Never refreshed" only when the server says so.
const refreshedText = (lastRefreshedAt: string | null | undefined) => {
  if (lastRefreshedAt === undefined) return '';
  if (lastRefreshedAt === null) return 'Never refreshed';
  return `Last refreshed: ${parseUTCdateTime(lastRefreshedAt, true)} UTC`;
};

export const RefreshBar: FC<RefreshBarProps> = ({ lastRefreshedAt, state }) => {
  const [requestState, requestRefresh] = useAtom(requestRefreshLoadableAtom);
  const outcome = useAtomValue(refreshOutcomeAtom);
  const busy = state !== 'idle' || requestState.state === 'loading';
  const neverRefreshed = lastRefreshedAt === null;

  return (
    <>
      <div className={styles.refreshBar}>
        <span className={styles.refreshedAt}>
          {refreshedText(lastRefreshedAt)}
        </span>
        {state !== 'idle' && (
          <InlineLoading
            className={styles.refreshStatus}
            status="active"
            description={STATUS[state]}
          />
        )}
        <Button
          kind="tertiary"
          size="sm"
          renderIcon={Renew}
          disabled={busy}
          onClick={() => requestRefresh()}
        >
          Refresh now
        </Button>
      </div>

      {neverRefreshed && state === 'idle' && (
        <InlineNotification
          kind="info"
          lowContrast
          hideCloseButton
          title="Not measured yet"
          subtitle="The percent of each assignment's producing segment in its unit has not been computed. Refresh now to measure every assignment; it takes about a minute, and runs nightly after that."
        />
      )}

      {outcome?.kind === 'failed' && state === 'idle' && (
        <InlineNotification
          kind="warning"
          lowContrast
          hideCloseButton
          title="The refresh did not complete"
          subtitle={`${
            outcome.message ? `${outcome.message}. ` : ''
          }The measurements are still the ones from the last refresh. Try again; if it fails again, the refresh job's error is in Sidekiq.`}
        />
      )}

      {outcome?.kind === 'no_result' && state === 'idle' && (
        <InlineNotification
          kind="info"
          lowContrast
          hideCloseButton
          title="No result from the refresh yet"
          subtitle="It has not finished after 15 minutes. It may still be queued behind other background work; reload the page later to see it."
        />
      )}

      {requestState.state === 'hasError' && (
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          title="Could not start the refresh"
          subtitle={
            requestState.error instanceof Error
              ? requestState.error.message
              : 'Please try again.'
          }
        />
      )}
    </>
  );
};
